import { $, $$, OverlayElement, debounce, fetchHTML, theme, prefersReducedMotion, toast } from './utils.js';
import './cart.js';
import './carousel.js';

if (theme.settings.wishlistEnabled) import('./wishlist.js');

/* ---------- header: transparent over hero -> solid after ~80px (IntersectionObserver, no scroll events) ---------- */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('[data-header]');
    const sentinel = document.getElementById('header-sentinel');
    if (!this.header || !sentinel || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      ([entry]) => this.header.classList.toggle('is-scrolled', !entry.isIntersecting),
      { threshold: 0 }
    );
    this.observer.observe(sentinel);
  }
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}
customElements.define('sticky-header', StickyHeader);

/* ---------- overlays ---------- */
class MenuDrawer extends OverlayElement {}
customElements.define('menu-drawer', MenuDrawer);

class SearchOverlay extends OverlayElement {
  connectedCallback() {
    super.connectedCallback();
    this.input = $('[data-search-input]', this);
    this.results = $('[data-search-results]', this);
    this.predictive = this.dataset.predictive === 'true';
    if (this.predictive && this.input) {
      this.input.addEventListener('input', debounce(() => this.search(), 220));
    }
  }

  async search() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.results.innerHTML = '';
      return;
    }
    const url = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=${Number(theme.settings && theme.settings.searchLimit) || 4}&resources[options][unavailable_products]=last`;
    try {
      const doc = await fetchHTML(url);
      const section = doc.querySelector('[data-predictive-results]');
      if (this.input.value.trim() !== term) return;
      this.results.innerHTML = section ? section.innerHTML : '';
    } catch (error) {
      this.results.innerHTML = '';
    }
  }
}
customElements.define('search-overlay', SearchOverlay);

/* ---------- announcement bar: rotating messages + optional dismiss ---------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const KEY = 'moderie:announcement-dismissed';
    try {
      if (this.dataset.dismissible === 'true' && sessionStorage.getItem(KEY)) {
        this.hideBar();
        return;
      }
    } catch (e) {}
    const dismiss = $('[data-dismiss]', this);
    if (dismiss) {
      dismiss.addEventListener('click', () => {
        try {
          sessionStorage.setItem(KEY, '1');
        } catch (e) {}
        this.hideBar();
      });
    }
    this.items = $$('.msg-item', this);
    this.index = 0;
    if (this.items.length < 2) return;
    this.interval = parseInt(this.dataset.interval, 10) || 5000;
    ['mouseenter', 'focusin'].forEach((name) => this.addEventListener(name, () => (this.paused = true)));
    ['mouseleave', 'focusout'].forEach((name) => this.addEventListener(name, () => (this.paused = false)));
    this.timer = setInterval(() => {
      if (!this.paused && !prefersReducedMotion()) this.show(this.index + 1);
    }, this.interval);
  }
  show(next) {
    this.items[this.index].classList.remove('is-active');
    this.index = (next + this.items.length) % this.items.length;
    this.items[this.index].classList.add('is-active');
  }
  hideBar() {
    this.hidden = true;
    document.documentElement.style.setProperty('--announcement-h', '0px');
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ---------- animated <details> accordions ---------- */
class AccordionGroup extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;
      const details = summary.parentElement;
      if (details.tagName !== 'DETAILS' || prefersReducedMotion() || !details.animate) return;
      event.preventDefault();
      const body = $('.body', details);
      if (!body) {
        details.open = !details.open;
        return;
      }
      if (details.open) {
        const height = body.offsetHeight;
        const animation = body.animate([{ height: height + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 220, easing: 'ease' });
        body.style.overflow = 'hidden';
        animation.onfinish = () => {
          details.open = false;
          body.style.overflow = '';
        };
      } else {
        details.open = true;
        const height = body.offsetHeight;
        body.style.overflow = 'hidden';
        const animation = body.animate([{ height: '0px', opacity: 0 }, { height: height + 'px', opacity: 1 }], { duration: 240, easing: 'ease' });
        animation.onfinish = () => (body.style.overflow = '');
      }
    });
  }
}
customElements.define('accordion-group', AccordionGroup);

/* ---------- small enhancements ---------- */
document.addEventListener('change', (event) => {
  const select = event.target.closest('select[data-auto-submit]');
  if (select && select.form) select.form.submit();
});

/* Newsletter forms: show a toast on success (the section also renders an inline message). */
if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
  toast(theme.strings.thanksSubscribe);
}